import React, {useCallback, useMemo, useState} from "react";
import {WarrantyAndReturnRequestStatuses} from "../../../../../core/constants/enums";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../core/services/api/api";
import UpsertPartSupportStatusDialog from "../../../dialogs/upsert-part-support-status";
import ChangeWarrantyRGAModal from "../../../dialogs/change-warranty-rga";
import AssignCreditToPartSupportRequestModal from "../../../dialogs/assign-credit-to-part-support-request";
import AssignVendorCreditToPartSupportRequestModal from "../../../dialogs/assign-vendor-credit-to-part-support-request";
import PartSupportVendorSelection from "../part-support-vendor-selection";
import UpdatePartSupportRequestNoteModal from "../../../dialogs/update-part-support-request-note";
import VendorInformationModal from "../../../dialogs/vendor-information";
import ToastService from "../../../../../core/services/toast";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes} from "../../../../../packages/data-grid";
import {PartSupportRequestsDataGridCellKeys} from "../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../base/button/icon_button";
import {CircularProgress, SvgIcon, Tooltip} from "@mui/material";
import {Edit, OpenInNew} from "@mui/icons-material";
import {ReactComponent as Delete} from "../../../../../assets/images/remove.svg";
import Button from "../../../base/button/button";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../containers/data-grid";
import {ReactComponent as Printer} from "../../../../../assets/images/printer.svg";
import {Popover} from "../../popover";
import {Col, Row} from "reactstrap";
import UpsertPartSupportVendorStatusDialog from "../../../dialogs/upsert-part-support-vendor-status";
import ChangePartSupportOperatorDialog from "../../../dialogs/change-part-support-operator";
import useAccessLevels from "../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../draggable-popover/product-information-popover";
import {Link, useHistory} from "react-router-dom";
import {useOperatorInformation} from "../../../../hooks/use-operator-information";

const initialAssignCredit = {open: false, request: undefined,}
const initialAssignVendor = {open: false, request: undefined, onSelect: undefined}
const initialAssignVendorCredit = {open: false, request: undefined}
const initialUpdateNote = {open: false, request: undefined}
const initialVendorInfo = {open: false, request: undefined}
const initialChangeStatus = {open: false, request: undefined}
const initialChangeVendorStatus = {open: false, requestId: undefined, statusId: undefined}
const initialChangeRga = {open: false, request: undefined}
const initialChangeOperator = {open: false, operator: undefined, requestId: undefined}

const PartSupportRequestInfoTable = ({
                                         makes,
                                         models,
                                         data,
                                         exclude: excludeProps = [],
                                         allowSorting = false,
                                         allowNavigation = false,
                                         disableInteractions = false,
                                         showRequestTemplateSubmission,
                                         setRequestProperty,
                                         statuses,
                                         tableProps = {},
                                     }) => {
    const operatorAccessLevels = useAccessLevels();
    const history = useHistory();
    const [changeStatus, setChangeStatus] = useState(initialChangeStatus);
    const [changeVendorStatus, setChangeVendorStatus] = useState(initialChangeVendorStatus);
    const [changeRga, setChangeRga] = useState(initialChangeRga);
    const [assignCredit, setAssignCredit] = useState(initialAssignCredit);
    const [assignVendor, setAssignVendor] = useState(initialAssignVendor);
    const [assignVendorCredit, setAssignVendorCredit] = useState(initialAssignVendorCredit);
    const [changeOperator, setChangeOperator] = useState(initialChangeOperator);
    const [updateNote, setUpdateNote] = useState(initialUpdateNote);
    const [vendorInfo, setVendorInfo] = useState(initialVendorInfo);
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();


    const exclude = useMemo(() => [
        ...(excludeProps ?? []),
        ...(allowNavigation ? [] : [PartSupportRequestsDataGridCellKeys.navigate]),
    ], [excludeProps, allowNavigation])

    /**
     * Assigns a request to the operator by calling the server api.
     * @param request {any} the request to be assigned to operator
     */
    const acceptRequest = useCallback(async (request) => {
        setRequestProperty(request, {accepting: true});
        const response = await Api.assignPartSupportRequestToOperator(request.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRequestProperty(request, {
                operator: {
                    id: operatorInformation?.id,
                    firstName: operatorInformation?.firstName ?? "",
                    lastName: operatorInformation?.lastName ?? "",
                },
                statusID: response.data,
                status: statuses.find(e => e.id === response.data)?.title,
            });
        }
        setRequestProperty(request, {accepting: false});
    }, [isMounted, operatorInformation?.firstName, operatorInformation?.id, operatorInformation?.lastName, setRequestProperty, statuses])

    /**
     * Removes the vendor from the part support request.
     * @type {function(any, boolean): Promise<void>}
     */
    const removeVendor = useCallback(async (request, removeMessages = false) => {
        setRequestProperty(request, {removingVendor: true});
        const forApi = {
            partSupportReqId: request.id,
            isChatBeingRemoved: removeMessages
        }
        const response = await Api.removeVendorFromPartSupportRequest(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRequestProperty(request, {
                vendor: null,
                vendorAccessLink: null,
                vendorCreditNumber: null,
                vendorCreditId: null,
            });
        }
        setRequestProperty(request, {removingVendor: false});
    }, [isMounted, setRequestProperty])

    /**
     * Removes the warehouse credit from the request.
     *
     * if the response of the api is successful, then updates the list state.
     * @type {function(any): void}
     */
    const removeWarehouseCredit = useCallback(async (request) => {
        setRequestProperty(request, {removingCredit: true});
        const forApi = {
            partSupportReqId: request.id,
        }
        const response = await Api.detachPartSupportRequestCredit(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setRequestProperty(request, {creditId: null, creditNo: null,});
        }
        setRequestProperty(request, {removingCredit: false});
    }, [isMounted, setRequestProperty])

    /**
     * Removes the vendor credit from the request.
     *
     * if the response of the api is successful, then updates the list state.
     * @type {function(any): void}
     */
    const removeVendorCredit = useCallback(async (request) => {
        setRequestProperty(request, {removingVendorCredit: true});
        const forApi = {
            partSupportReqId: request.id,
        }
        const response = await Api.detachPartSupportRequestVendorCredit(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRequestProperty(request, {vendorCreditNumber: null, vendorCreditId: null,});
        }
        setRequestProperty(request, {removingVendorCredit: false});
    }, [isMounted, setRequestProperty])

    /**
     * Navigates the operator to the single page of the request.
     *
     * if the request's status is initial, then opens the documentation dialog
     * @param request
     */
    const navigateToPartSupportInformationView = useCallback((request) => {
        if (request.statusID !== WarrantyAndReturnRequestStatuses.initial) {
            return history.push(routeFunctions.warrantyAndReturn.single(request.id));
        }
        if (!request.customerId && !request.warehouseCode) {
            return ToastService.error({
                title: "No Customer or Warehouse Found",
                message: "The selected request does not belong to any customers or is not warehouse based. Please contact IT support.",
            });
        }
        if (!operatorAccessLevels.packages.warrantyAndReturn.submitRequest) {
            return ToastService.error({
                title: "Access Denied",
                message: "This request requires you to submit documentations for it, that you do not have access to.",
            });
        }
        showRequestTemplateSubmission(request).then();
    }, [history, operatorAccessLevels.packages.warrantyAndReturn.submitRequest, showRequestTemplateSubmission])

    /**
     * Navigates the operator to the print screen of a part support request
     * @param request
     */
    const printRequest = (request) => {
        window.open(window.location.origin + routeFunctions.print.partSupport(request.id), '_blank')
    }

    /**
     * Opens the modal for changing the status of the request
     * @type {function(any): void}
     */
    const openChangeRgaDialog = useCallback((request) => {
        setChangeRga({
            open: true,
            request: request,
        })
    }, [])

    /**
     * Opens the modal for changing the status of the request
     * @type {function(any): void}
     */
    const openChangeStatusDialog = useCallback((request) => {
        setChangeStatus({
            open: true,
            request: request,
        })
    }, [])

    /**
     * Opens the dialog for changing the vendor status of the request
     * @type {function(any): void}
     */
    const openChangeVendorStatusDialog = useCallback((request) => {
        setChangeVendorStatus({
            open: true,
            requestId: request.id,
            statusId: request.vendorStatusId,
        })
    }, [])

    /**
     * Opens the modal for seeing and manipulating the vendor information of the request.
     * @type {function(any): void}
     */
    const openVendorInfoDialog = useCallback((request) => {
        setVendorInfo({
            open: true,
            request: request,
        })
    }, [])

    /**
     * Adds the assigned vendor information to the request.
     * @type {function(any, any): any}
     */
    const assignVendorToRequest = useCallback((request, data) => {
        const res = {
            ...request,
            vendor: data.vendorInfo,
            vendorAccessLink: data.vendorAccessLink,
        };
        setRequestProperty(request, res)
        return res
    }, [setRequestProperty])

    /**
     * Opens the assign vendor to request dialog.
     * @type {function(any): void}
     */
    const openAssignVendorDialog = useCallback((request) => {
        setAssignVendor({
            open: true,
            request: request,
            onSelect: (data) => assignVendorToRequest(request, data)
        })
    }, [assignVendorToRequest])

    /**
     * Opens the update request note dialog.
     * @type {function(any): void}
     */
    const openUpdateRequestNoteDialog = useCallback((request) => {
        setUpdateNote({
            open: true,
            request: request,
        })
    }, [])

    /**
     * Opens the assign vendor credit to request dialog.
     *
     * if the request does not have a vendor, first shows the assign vendor to request dialog and only upon its
     * completion, proceeds with the vendor credit to request dialog.
     * @type {function(any): void}
     */
    const openConnectToVendorCreditDialog = useCallback((request) => {
        const restOfLogic = () => {
            setAssignVendorCredit({
                open: true,
                request: request,
            })
        }
        if (!request?.vendor) {
            return setAssignVendor({
                open: true,
                request: request,
                onSelect: (data) => {
                    request = assignVendorToRequest(request, data);
                    restOfLogic();
                }
            })
        }
        restOfLogic();
    }, [assignVendorToRequest])

    /**
     * Asks for the user confirmation to decide whether to remove the associated messages as well or not.
     * - in case of a decision, removes the vendor.
     * @param {any} request the request that the op is to be performed on
     */
    const openRemoveConfirmationDialogForVendor = useCallback((request) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove the selected vendor from this request? If you want to delete
                    it, you can either remove the vendor only or remove it with all its related messages.
                    Note that the assigned vendor credit will also be removed
                </p>
            </div>,
            {
                maxWidth: 'md'
            },
            (confirmed) => removeVendor(request, confirmed),
            "Remove the Vendor and related messages",
            "Remove the Vendor Only"
        );
    }, [removeVendor])

    /**
     * Asks for the user confirmation to decide whether to remove the associated operation or not.
     * - in case of a confirmation, proceeds with the operation.
     *
     * @param {any} request the request that the op is to be performed on
     * @param {function} operation the operation done in the request if confirmed.
     * @param {string} name
     */
    const openRemoveConfirmationDialog = useCallback((request, operation, name) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to remove this {name} from this Part support Request?
                </p>
            </div>,
            {maxWidth: 'sm'},
            (confirm) => confirm && operation(request),
        );
    }, [])

    /**
     * Opens the assign credit to request dialog.
     */
    const openChangeOperatorDialog = useCallback(async (request) => {
        setChangeOperator({
            open: true,
            operator: request.operator ?? null,
            requestId: request.id,
        })
    }, [])

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                ...(operatorAccessLevels.packages.warrantyAndReturn.viewDetails ? [
                    {
                        name: PartSupportRequestsDataGridCellKeys.navigate,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        pinned: true,
                        sortable: false,
                        pinnedToggleable: false,
                        resizable: false,
                        visibilityToggleable: false,
                        width: 0,
                    },
                    ...(operatorAccessLevels.packages.bizPrint.printWarrantyAndReturnClaim ? [
                            {
                                name: PartSupportRequestsDataGridCellKeys.print,
                                alignment: DataGridColumnAlignments.center,
                                type: DataGridColumnTypes.element,
                                pinned: true,
                                sortable: false,
                                pinnedToggleable: true,
                                resizable: false,
                                visibilityToggleable: false,
                                width: 0,
                            },
                        ] : []
                    ),
                ] : []),
                {
                    title: "RGA",
                    name: PartSupportRequestsDataGridCellKeys.rga,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                },
                {
                    title: "Operator",
                    name: PartSupportRequestsDataGridCellKeys.operator,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 80,
                },
                {
                    title: "Status",
                    name: PartSupportRequestsDataGridCellKeys.status,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    width: 120,
                },
                {
                    title: "Vendor Status",
                    name: PartSupportRequestsDataGridCellKeys.vendorStatus,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    width: 120,
                },
                {
                    title: "Customer",
                    name: PartSupportRequestsDataGridCellKeys.customer,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                },
                {
                    title: "Part",
                    name: PartSupportRequestsDataGridCellKeys.partNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    width: 95,
                },
                {
                    title: "Serial No",
                    name: PartSupportRequestsDataGridCellKeys.serialNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                },
                {
                    title: "Reason",
                    name: PartSupportRequestsDataGridCellKeys.reason,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 85
                },
                {
                    title: "Invoice No",
                    name: PartSupportRequestsDataGridCellKeys.invoiceNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.number,
                    width: 85,
                },
                {
                    title: "Customer Credit",
                    name: PartSupportRequestsDataGridCellKeys.credit,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 130,
                },
                {
                    title: "Vendor Credit",
                    name: PartSupportRequestsDataGridCellKeys.vendorCredit,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 113,
                },
                {
                    title: "Vendor",
                    name: PartSupportRequestsDataGridCellKeys.vendor,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 122,
                },
                {
                    title: "Type",
                    name: PartSupportRequestsDataGridCellKeys.type,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 44,
                },
                {
                    title: "Warehouse code",
                    name: PartSupportRequestsDataGridCellKeys.warehouseCode,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 122,
                },
                {
                    title: "Count",
                    name: PartSupportRequestsDataGridCellKeys.count,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.number,
                    width: 51,
                },
                {
                    title: "User",
                    name: PartSupportRequestsDataGridCellKeys.user,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 63,
                },
                {
                    title: "Submit Date",
                    name: PartSupportRequestsDataGridCellKeys.submitDateTime,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.date,
                    width: 93,
                },
                {
                    title: "Note",
                    name: PartSupportRequestsDataGridCellKeys.note,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: allowSorting ? 500 : 350,
                },
                {
                    title: "Internal Note",
                    name: PartSupportRequestsDataGridCellKeys.internalNote,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 100,
                },
                {
                    title: "Make",
                    name: PartSupportRequestsDataGridCellKeys.make,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 100,
                },
                {
                    title: "Model",
                    name: PartSupportRequestsDataGridCellKeys.model,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 100,
                },
                {
                    title: "Year",
                    name: PartSupportRequestsDataGridCellKeys.year,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 70,
                },
                {
                    title: "VIN",
                    name: PartSupportRequestsDataGridCellKeys.vin,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.string,
                    width: 120,
                },
                {
                    title: "Installed Date",
                    name: PartSupportRequestsDataGridCellKeys.installedDate,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.date,
                    width: 150,
                },
                {
                    title: "Failed Date",
                    name: PartSupportRequestsDataGridCellKeys.failedDate,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.date,
                    width: 150,
                },
                {
                    title: "Installed Mileage",
                    name: PartSupportRequestsDataGridCellKeys.installedMileage,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.number,
                    width: 150,
                },
                {
                    title: "Failed Mileage",
                    name: PartSupportRequestsDataGridCellKeys.failedMileage,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.number,
                    width: 150,
                },
                {
                    title: "Remarks",
                    name: PartSupportRequestsDataGridCellKeys.remarks,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 200,
                },
            ]
                .filter(e => !exclude.includes(e.name))
                .map(e => ({
                    ...e,
                    sortable: allowSorting ? e.sortable : false
                })),
        [operatorAccessLevels.packages.warrantyAndReturn.viewDetails, operatorAccessLevels.packages.bizPrint.printWarrantyAndReturnClaim, allowSorting, exclude])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: tableProps?.saveConfig?.name,
        version: tableProps?.saveConfig?.version,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data
            ?.map((request) => {
                return ({
                    key: request?.id,
                    data: request,
                    cells: {
                        [PartSupportRequestsDataGridCellKeys.navigate]:
                            allowNavigation &&
                            <IconButton
                                disabled={!request || disableInteractions}
                                color={'secondary'}
                                {...(request.statusID !== WarrantyAndReturnRequestStatuses.initial) && {
                                    component: Link,
                                    to: routeFunctions.warrantyAndReturn.single(request.id),
                                }}
                                {...(request.statusId === WarrantyAndReturnRequestStatuses.initial) && {
                                    onPressed: () => navigateToPartSupportInformationView(request),
                                }}
                            >
                                <OpenInNew className='icon normal'/>
                            </IconButton>,
                        [PartSupportRequestsDataGridCellKeys.print]:
                            <Tooltip title={'Print'} className={'mr-2'}>
                                <span>
                                    <IconButton
                                        onPressed={() => printRequest(request)}
                                        disabled={!request || disableInteractions}
                                        color={'secondary'}
                                        className={'email-icon-container'}
                                    >
                                    <SvgIcon className='icon medium' component={Printer}/>
                                </IconButton>
                                </span>
                            </Tooltip>,
                        [PartSupportRequestsDataGridCellKeys.rga]: {
                            value: <>
                                    {
                                        operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                        <IconButton
                                            disabled={disableInteractions}
                                            className={'mr-2'}
                                            color={'secondary'}
                                            onPressed={() => openChangeRgaDialog(request)}>
                                            <Edit className={'icon small'}/>
                                        </IconButton>
                                    }
                                    <p {...dataGridCellTextProps} className={'text-sm'}>
                                        {request.rga ?? '--'}
                                    </p>
                                </>,
                            copyValue: request.rga,
                        },
                        [PartSupportRequestsDataGridCellKeys.type]: request?.type?.name ?? request?.type ?? '',
                        [PartSupportRequestsDataGridCellKeys.status]:
                            <>
                                {
                                    operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                    <IconButton
                                        disabled={disableInteractions}
                                        className={'mr-1'}
                                        color={'secondary'}
                                        onPressed={() => openChangeStatusDialog(request)}>
                                        <Edit className={'icon small'}/>
                                    </IconButton>
                                }
                                <p {...dataGridCellTextProps} className={'text-sm'}>
                                    {request.status ?? '--'}
                                </p>
                            </>,
                        [PartSupportRequestsDataGridCellKeys.vendorStatus]:
                            <>
                                {
                                    operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                    <IconButton
                                        disabled={disableInteractions}
                                        className={'mr-1'}
                                        color={'secondary'}
                                        onPressed={() => openChangeVendorStatusDialog(request)}>
                                        <Edit className={'icon small'}/>
                                    </IconButton>
                                }
                                <p {...dataGridCellTextProps} className={'text-sm'}>
                                    {request.vendorStatus ?? '--'}
                                </p>
                            </>,
                        [PartSupportRequestsDataGridCellKeys.reason]: request?.statusChangeReason !== null
                            ? `${request?.statusChangeReason?.title ?? ""} (${request?.statusChangeReason?.code ?? '--'})`
                            : '',
                        [PartSupportRequestsDataGridCellKeys.partNo]: {
                            value: <ProductInformationPopover partNo={request.partNo}>
                                {request.partNo ?? ''}
                            </ProductInformationPopover>,
                            copyValue: request.partNo,
                        },
                        [PartSupportRequestsDataGridCellKeys.credit]:
                            !request?.customer
                                ? null
                                : request?.creditId
                                    ? <>
                                        {
                                            operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                            <IconButton
                                                onPressed={() => openRemoveConfirmationDialog(request, removeWarehouseCredit, "Credit")}
                                                disabled={request?.removingCredit}
                                                color={'paper.error'}
                                            >
                                                {
                                                    request?.removingCredit
                                                        ? <CircularProgress size={12}/>
                                                        : <Delete className='icon small remove-icon'/>
                                                }

                                            </IconButton>
                                        }
                                        <p className={'ml-1'}>{request?.creditNo ?? '--'}</p>
                                    </>
                                    : operatorAccessLevels.packages.warrantyAndReturn.editRequest && <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={() => setAssignCredit({open: true, request: request})}
                                    disabled={disableInteractions}
                                >
                                    Connect
                                </Button>,
                        [PartSupportRequestsDataGridCellKeys.vendorCredit]: request?.vendorCreditId
                            ? <>
                                {
                                    operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                    operatorAccessLevels.packages.warrantyAndReturn.manageVendor &&
                                    <IconButton
                                        onPressed={() => openRemoveConfirmationDialog(request, removeVendorCredit, 'Vendor' +
                                            ' Credit')}
                                        disabled={request?.removingVendorCredit}
                                        color={'paper.error'}
                                    >
                                        {
                                            request?.removingVendorCredit
                                                ? <CircularProgress size={12}/>
                                                : <Delete className='icon small remove-icon'/>
                                        }

                                    </IconButton>
                                }
                                <p {...dataGridCellTextProps} className={'ml-1'}>{request?.vendorCreditNumber ?? '--'}</p>
                            </>
                            : operatorAccessLevels.packages.warrantyAndReturn.editRequest && operatorAccessLevels.packages.warrantyAndReturn.manageVendor &&
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => openConnectToVendorCreditDialog(request)}
                                disabled={disableInteractions}
                            >
                                Connect
                            </Button>,
                        [PartSupportRequestsDataGridCellKeys.vendor]: !!request?.vendor
                            ? <>
                                <Popover
                                    child={(onEnter) => (
                                        <IconButton
                                            disabled={disableInteractions}
                                            className={'mr-2'}
                                            color={'secondary'}
                                            onPressed={onEnter}>
                                            <Edit className={'icon small'}/>
                                        </IconButton>
                                    )}
                                    popoverChild={(_, onLeave) => (
                                        <Row
                                            className='no-gutters align-items-center justify-content-center px-2'
                                            style={{maxWidth: '13rem'}}>
                                            <Col xs={12} className={'mx-2 my-2'}>
                                                <Button
                                                    onClick={() => onLeave() || openVendorInfoDialog(request)}
                                                    color={'secondary'}
                                                    variant={'text'}
                                                    className={'w-100'}
                                                >
                                                    View Information
                                                </Button>
                                            </Col>
                                            <Col xs={12} className={'mx-2 mb-2'}>
                                                {
                                                    operatorAccessLevels.packages.warrantyAndReturn.manageVendor &&
                                                    <Button
                                                        onClick={() => onLeave() || openRemoveConfirmationDialogForVendor(request)}
                                                        disabled={!!request?.removingVendor}
                                                        color={'error'}
                                                        variant={'text'}
                                                        className={'w-100'}
                                                    >
                                                        {
                                                            !request?.removingVendor
                                                                ? "Remove"
                                                                : "Removing..."
                                                        }
                                                    </Button>
                                                }
                                            </Col>
                                        </Row>
                                    )}
                                    anchorOrigin={{
                                        vertical: 'center',
                                        horizontal: 'center',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                />
                                <p>{request?.vendor?.name ?? '--'}</p>
                            </>
                            : operatorAccessLevels.packages.warrantyAndReturn.manageVendor && operatorAccessLevels.packages.warrantyAndReturn.manageVendor &&
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => openAssignVendorDialog(request)}
                                disabled={disableInteractions}
                            >
                                Connect
                            </Button>,
                        [PartSupportRequestsDataGridCellKeys.submitDateTime]: request.submitDateTime,
                        [PartSupportRequestsDataGridCellKeys.customer]: request.customer?.name ?? 'Warehouse Based',
                        [PartSupportRequestsDataGridCellKeys.warehouseCode]: request.warehouseCode,
                        [PartSupportRequestsDataGridCellKeys.user]: request.user
                            ? `${request.user?.firstName ?? '--'} ${request.user?.lastName ?? '--'}`
                            : null,
                        [PartSupportRequestsDataGridCellKeys.invoiceNo]: request.invoiceNo,
                        [PartSupportRequestsDataGridCellKeys.serialNo]: {
                            value: <p {...dataGridCellTextProps} className={'text-sm'}>
                                {request.serialNo ?? 'N/A'}
                            </p>,
                            copyValue: request.serialNo,
                        },
                        [PartSupportRequestsDataGridCellKeys.count]: request.unSeriliazablePartsCount ?? 'N/A',
                        [PartSupportRequestsDataGridCellKeys.operator]:
                            request.statusID === WarrantyAndReturnRequestStatuses.waitForOperator
                                ? operatorAccessLevels.packages.warrantyAndReturn.editRequest && <Button
                                disabled={request.accepting}
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => acceptRequest(request)}
                            >
                                {
                                    request?.accepting
                                        ? "Accepting..."
                                        : "Accept"
                                }
                            </Button>
                                : <>
                                    {
                                        operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                        <IconButton
                                            disabled={disableInteractions}
                                            className={'mr-2'}
                                            color={'secondary'}
                                            onPressed={() => openChangeOperatorDialog(request)}>
                                            <Edit className={'icon small'}/>
                                        </IconButton>
                                    }
                                    <p className={'text-sm'}>
                                        {
                                            request.statusID === WarrantyAndReturnRequestStatuses.initial
                                                ? "Requires Documents"
                                                : `${request.operator?.firstName ?? '--'} ${request.operator?.lastName ?? '--'}`
                                        }
                                    </p>
                                </>,
                        [PartSupportRequestsDataGridCellKeys.note]:
                            <div>
                                <p {...dataGridCellTextProps} className={'word-line-break-anywhere'}>
                                    {
                                        request?.note
                                            ? request?.note?.substring(0, Math.min(allowSorting ? 160 : 40, request.note.length))
                                            + (request.note.length > (allowSorting ? 160 : 40) ? '...' : '')
                                            : ''
                                    }
                                </p>
                                {
                                    operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                    <Button
                                        className={`m-1 d-inline-block`}
                                        variant={'outlined'}
                                        color={'primary'}
                                        onClick={() => openUpdateRequestNoteDialog(request)}
                                    >
                                        {
                                            !!request?.note
                                                ? "View"
                                                : "Create"
                                        }
                                    </Button>
                                }
                            </div>,
                        [PartSupportRequestsDataGridCellKeys.internalNote]:
                            <div>
                                <p {...dataGridCellTextProps}>
                                    {
                                        request?.internalNote
                                            ? request?.internalNote?.substring(0, Math.min(allowSorting ? 160 : 40, request.internalNote.length))
                                            + (request.internalNote.length > (allowSorting ? 160 : 40) ? '...' : '')
                                            : ''
                                    }
                                </p>
                                {
                                    operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                                    <Button
                                        className={`m-1 d-inline-block`}
                                        variant={'outlined'}
                                        color={'primary'}
                                        onClick={() => openUpdateRequestNoteDialog(request)}
                                    >
                                        {
                                            !!request?.internalNote
                                                ? "View"
                                                : "Create"
                                        }
                                    </Button>
                                }
                            </div>,
                        [PartSupportRequestsDataGridCellKeys.make]: !!request?.makeId ? makes?.find(e => e.id === request?.makeId)?.name : undefined,
                        [PartSupportRequestsDataGridCellKeys.model]: !!request?.modelId ? models?.find(e => e.id === request?.modelId)?.name : undefined,
                        [PartSupportRequestsDataGridCellKeys.year]: request?.year,
                        [PartSupportRequestsDataGridCellKeys.vin]: request?.vin,
                        [PartSupportRequestsDataGridCellKeys.installedDate]: request.installedDate,
                        [PartSupportRequestsDataGridCellKeys.failedDate]: request.failedDate,
                        [PartSupportRequestsDataGridCellKeys.installedMileage]: request.installedMileage,
                        [PartSupportRequestsDataGridCellKeys.failedMileage]: request.failedMileage,
                        [PartSupportRequestsDataGridCellKeys.remarks]: request.remarks,
                    }
                })
            });
    }, [data, allowNavigation, disableInteractions, operatorAccessLevels.packages.warrantyAndReturn.editRequest, operatorAccessLevels.packages.warrantyAndReturn.manageVendor, allowSorting, makes, models, navigateToPartSupportInformationView, openChangeRgaDialog, openChangeStatusDialog, openChangeVendorStatusDialog, openRemoveConfirmationDialog, removeWarehouseCredit, removeVendorCredit, openConnectToVendorCreditDialog, openVendorInfoDialog, openRemoveConfirmationDialogForVendor, openAssignVendorDialog, acceptRequest, openChangeOperatorDialog, openUpdateRequestNoteDialog])

    return (
        <>
            <PartnerDataGrid
                columns={dataGridColumns}
                initialLayoutState={{
                    columns: columns,
                }}
                rows={dataGridRows}
                state={tableProps.state ?? {}}
                {...tableProps.saveConfig ?? {}}
                {...(tableProps.layout ?? {})}
                {...(tableProps?.events ?? {})}
                {...tableProps?.misc ?? {}}
            />
            <UpsertPartSupportStatusDialog
                statuses={statuses}
                request={changeStatus.request}
                setOpen={() => setChangeStatus(initialChangeStatus)}
                open={changeStatus.open}
                onSuccess={(request, status, reason) => setRequestProperty(request, {
                    statusID: status.id,
                    status: status.title,
                    statusChangeReason: reason,
                })}
            />
            <UpsertPartSupportVendorStatusDialog
                open={changeVendorStatus.open}
                setOpen={() => setChangeVendorStatus(initialChangeVendorStatus)}
                requestId={changeVendorStatus.requestId}
                statusId={changeVendorStatus.statusId}
                onSuccess={(requestId, status, reason) => setRequestProperty({id: requestId}, {
                    vendorStatusId: status.id,
                    vendorStatus: status.title,
                    vendorStatusReasonId: reason?.id,
                    vendorStatusReason: reason?.title,
                })}
            />
            <ChangeWarrantyRGAModal
                request={changeRga.request}
                setOpen={() => setChangeRga(initialChangeRga)}
                open={changeRga.open}
                onSuccess={(request, rga) => setRequestProperty(request, {rga: rga})}
            />
            <AssignCreditToPartSupportRequestModal
                partSupportRequest={assignCredit.request}
                open={assignCredit.open}
                setOpen={() => setAssignCredit(initialAssignCredit)}
                onSuccess={(request, data) => setRequestProperty(request, data)}
            />
            <AssignVendorCreditToPartSupportRequestModal
                request={assignVendorCredit.request}
                open={assignVendorCredit.open}
                setOpen={() => setAssignVendorCredit(initialAssignVendorCredit)}
                onSuccess={(request, data) => setRequestProperty(request, {
                    vendorCreditNumber: data.purchaseNo,
                    vendorCreditId: data.id,
                })}
            />
            <ChangePartSupportOperatorDialog
                open={changeOperator.open}
                setOpen={() => setChangeOperator(initialChangeOperator)}
                operator={changeOperator.operator}
                requestId={changeOperator.requestId}
                onSuccess={(requestId, operator) => setRequestProperty({id: requestId}, {
                    operator: operator,
                })}
            />
            <PartSupportVendorSelection
                dialog
                request={assignVendor.request}
                onSelect={assignVendor.onSelect}
                open={assignVendor.open}
                setOpen={() => setAssignVendor(initialAssignVendor)}
            />
            <UpdatePartSupportRequestNoteModal
                request={updateNote.request}
                open={updateNote.open}
                setOpen={() => setUpdateNote(initialUpdateNote)}
                onSuccess={(request, note, internalNote) => setRequestProperty(request, {note: note, internalNote: internalNote})}
            />
            <VendorInformationModal
                request={vendorInfo.request}
                vendor={vendorInfo.request?.vendor}
                open={vendorInfo.open}
                setOpen={() => setVendorInfo(initialVendorInfo)}
                setNewLink={(request, newLink) => setRequestProperty(request, {vendorAccessLink: newLink})}
            />
        </>
    )
}

export default PartSupportRequestInfoTable;
